// types
import { AccountPermission, AccountRole, AccountSummary } from 'types/console';

// ==============================|| ACCOUNT PERMISSIONS ||============================== //
//
// Mirror of Hub's `internal/app/authz.go` (contract V2). Presentation only: it decides which buttons and
// menu entries appear, and Hub checks every call again.

export const ACCOUNT_ROLES: AccountRole[] = ['owner', 'admin', 'developer', 'editor', 'tester_manager', 'viewer'];

const ALL: AccountPermission[] = [
  'read',
  'account.manage',
  'account.delete',
  'ownership.transfer',
  'team.manage',
  'keys.manage',
  'products.create',
  'product.edit',
  'content.edit',
  'content.publish',
  'releases.manage',
  'releases.publish',
  'testers.manage',
  'review.submit'
];

const MATRIX: Record<AccountRole, AccountPermission[]> = {
  owner: ALL,
  admin: ALL.filter((permission) => permission !== 'account.delete' && permission !== 'ownership.transfer'),
  developer: [
    'read',
    'products.create',
    'product.edit',
    'content.edit',
    'content.publish',
    'releases.manage',
    'releases.publish',
    'testers.manage',
    'review.submit',
    'keys.manage'
  ],
  editor: ['read', 'product.edit', 'content.edit'],
  tester_manager: ['read', 'testers.manage'],
  viewer: ['read']
};

/** Permissions that a member's `productIds` restriction narrows to those products. */
const PRODUCT_SCOPED: AccountPermission[] = [
  'product.edit',
  'content.edit',
  'content.publish',
  'releases.manage',
  'releases.publish',
  'testers.manage',
  'review.submit'
];

/** Roles whose reach can be narrowed to some products. Owner and admin always see everything. */
export const RESTRICTABLE_ROLES: AccountRole[] = ['developer', 'editor', 'tester_manager', 'viewer'];

export const isRestrictable = (role: AccountRole): boolean => RESTRICTABLE_ROLES.includes(role);

/**
 * Whether a membership holds a permission.
 *
 * `productId` follows the server's wording: omitted means "for some product" (used to decide whether a
 * control is worth showing at all), a product id means that product, and `null` means a resource bound to
 * no product (an account-wide API key). A member restricted to some products lacks `products.create`, and
 * holds `keys.manage` only for keys bound to one of its products.
 */
export const roleCan = (
  role: AccountRole,
  productIds: string[] | null,
  permission: AccountPermission,
  productId?: string | null
): boolean => {
  if (!MATRIX[role]?.includes(permission)) return false;

  if (productIds === null) return true;

  if (permission === 'products.create') return false;

  if (permission === 'keys.manage') {
    if (productId === undefined) return true;

    return productId !== null && productIds.includes(productId);
  }

  if (PRODUCT_SCOPED.includes(permission)) {
    if (productId === undefined) return true;

    return productId !== null && productIds.includes(productId);
  }

  return true;
};

export const accountCanFor = (account: AccountSummary | null, permission: AccountPermission, productId?: string | null): boolean =>
  account ? roleCan(account.role, account.productIds, permission, productId) : false;

const rank = (role: AccountRole): number => {
  const index = ACCOUNT_ROLES.indexOf(role);

  return index === -1 ? ACCOUNT_ROLES.length : index;
};

/** Roles an actor may hand out: strictly below their own, and never `owner` (that is a transfer). */
export const grantableAccountRoles = (actor: AccountRole | null | undefined): Exclude<AccountRole, 'owner'>[] => {
  if (!actor) return [];

  return ACCOUNT_ROLES.filter((role): role is Exclude<AccountRole, 'owner'> => role !== 'owner' && rank(role) > rank(actor));
};

/** Whether an actor may change or remove an existing member holding `target`: only members below them. */
export const outranksMember = (actor: AccountRole | null | undefined, target: AccountRole): boolean => {
  if (!actor || target === 'owner') return false;

  return rank(target) > rank(actor);
};
